// Shared helpers for the visual Template Builder (questions + documents).
import { Type, AlignLeft, List, CheckSquare, ToggleLeft, Hash, DollarSign, Calendar, MapPin, Mail, Phone, Layers, SquareCheck } from 'lucide-react';

export const TYPES = [
  ['text', 'Short text', Type, 'A single line — names, employer, suburb'],
  ['textarea', 'Long text', AlignLeft, 'A paragraph of free text'],
  ['select', 'Dropdown', List, 'Pick one answer from a list'],
  ['multi', 'Multiple choice', CheckSquare, 'Tick every answer that applies'],
  ['yesno', 'Yes / No', ToggleLeft, 'Two big buttons'],
  ['checkbox', 'Checkbox', SquareCheck, 'A single tick box'],
  ['number', 'Number', Hash, 'Whole numbers'],
  ['currency', 'Currency', DollarSign, 'Dollar amounts'],
  ['date', 'Date', Calendar, 'Date picker'],
  ['address', 'Address', MapPin, 'Address lookup'],
  ['email', 'Email', Mail, 'Validated email'],
  ['phone', 'Phone', Phone, 'Australian mobile / landline'],
  ['repeat', 'Repeating group', Layers, 'Add one or more entries (jobs, properties, debts)'],
];
export const TYPE_LABEL = Object.fromEntries(TYPES.map(([k, l]) => [k, l]));
export const TYPE_ICON = Object.fromEntries(TYPES.map(([k, , I]) => [k, I]));

export const OPS = [['eq', 'is'], ['neq', 'is not'], ['in', 'is one of'], ['truthy', 'is answered'], ['empty', 'is blank']];
export const DOC_OPS = [...OPS, ['lt', 'is less than'], ['months_lt', 'was less than N months ago'], ['months_gte', 'was N or more months ago']];
export const opLabel = (op, ops = DOC_OPS) => ops.find(([k]) => k === op)?.[1] || op;

export const DOC_GROUPS = ['Identification', 'Income', 'Assets & property', 'Liabilities', 'Other'];
export const guessGroup = (r) => {
  const s = `${r.item_key || ''} ${r.doc?.name || ''} ${r.name || ''}`.toLowerCase();
  if (/identity|licence|license|passport|photo id|medicare/.test(s)) return 'Identification';
  if (/payslip|income|tax|payg|salary|employment|contract|business|financial|rental|lease|centrelink|pension/.test(s)) return 'Income';
  if (/property|mortgage|home loan|rates|contract of sale|land|building|gift|savings|asset/.test(s)) return 'Assets & property';
  if (/liabilit|credit card|loan|debt|hecs|statement/.test(s)) return 'Liabilities';
  return 'Other';
};
export const ruleGroup = (r) => r.group || guessGroup(r);
// Stamp a group on every rule so the customer's upload page can show category headings consistently.
export const normalizeTemplate = (t) => (t ? { ...t, document_rules: (t.document_rules || []).map((r) => (r.group ? r : { ...r, group: guessGroup(r) })) } : t);

export const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'q';
export const uniqKey = (base, taken) => { let k = base; let n = 2; while (taken.includes(k)) k = `${base}_${n++}`; return k; };
export const clone = (x) => JSON.parse(JSON.stringify(x));

export const newQuestion = (type, label, scope) => {
  const f = { key: slug(label), label, type, scope, required: true, visibility: 'required' };
  if (['select', 'multi'].includes(type)) f.options = [{ value: 'option_1', label: 'Option 1' }, { value: 'option_2', label: 'Option 2' }];
  if (type === 'yesno') f.options = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
  if (type === 'repeat') { f.fields = [{ key: 'description', label: 'Description', type: 'text', required: true }]; f.add_label = 'Add another'; }
  return f;
};

export const newRule = (name, extra = {}) => ({
  id: `r_${slug(name)}_${Date.now().toString(36)}`, name: `Request ${name}`, item_key: slug(name),
  doc: { name, guidance: '', alternative: '', examples: '', types: ['pdf', 'jpg', 'png'], period: null, count: 1 },
  scope: 'applicant', required: true, for_each: null, when: [], explanation: '', enabled: true, group: 'Other', ...extra,
});

// Every question in the template, as condition targets. `fromSection` makes same-section refs relative (how show_if is stored).
export const allQuestions = (t, fromSection = null) => t.sections.flatMap((s) => (s.fields || []).map((f) => ({
  ref: fromSection && s.key === fromSection ? f.key : `@${s.key}.${f.key}`, abs: `@${s.key}.${f.key}`, field: f, section: s,
  label: f.label, sectionTitle: s.title,
})));

export const findQuestion = (t, ref, fromSection = null) => {
  const abs = ref?.startsWith('@') ? ref : `@${fromSection}.${ref}`;
  return allQuestions(t).find((q) => q.abs === abs) || null;
};

const valueLabel = (q, v) => { const o = (q?.field?.options || []).find((x) => x.value === v); return o ? o.label : (v ?? ''); };
// Plain-English rendering: "Employment type is Self employed"
export const describeCondition = (t, c, fromSection = null) => {
  if (!c?.field) return '';
  if (c.field.startsWith('row.')) return `each entry's ${c.field.slice(4).replace(/_/g, ' ')} ${opLabel(c.op)} ${c.value ?? ''}`.trim();
  const q = findQuestion(t, c.field, fromSection);
  const name = q ? q.label : c.field.replace(/^@/, '').replace('.', ' → ').replace(/_/g, ' ');
  if (['truthy', 'empty'].includes(c.op)) return `${name} ${opLabel(c.op)}`;
  const val = Array.isArray(c.value) ? c.value.map((v) => valueLabel(q, v)).join(' or ') : valueLabel(q, c.value);
  return `${name} ${opLabel(c.op)} ${val}`;
};

export const describeRule = (t, r) => {
  const when = (r.when || []).map((c) => describeCondition(t, c)).filter(Boolean);
  const each = r.for_each ? `for every ${findQuestion(t, r.for_each)?.label?.toLowerCase() || 'entry'}` : '';
  return when.length ? `IF ${when.join(' AND ')} THEN request ${r.doc?.name || r.name}${each ? ` (${each})` : ''}` : `Always request ${r.doc?.name || r.name}${each ? ` (${each})` : ''}`;
};

const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };
// Customer scenarios for the preview — pre-filled answers keyed by section.
export const SCENARIOS = [
  { key: 'blank', label: 'Start from scratch', answers: {} },
  { key: 'payg', label: 'Single · PAYG employee', answers: { objective: { applicant_count: 'me_only' }, employment: { employment_type: 'full_time', start_date: monthsAgo(18) } } },
  { key: 'new_job', label: 'Single · new PAYG job (<3 months)', answers: { objective: { applicant_count: 'me_only' }, employment: { employment_type: 'full_time', start_date: monthsAgo(1) } } },
  { key: 'self_employed', label: 'Single · self-employed', answers: { objective: { applicant_count: 'me_only' }, employment: { employment_type: 'self_employed', start_date: monthsAgo(40), has_businesses: 'yes' } } },
  { key: 'owner', label: 'Single · owns property', answers: { objective: { applicant_count: 'me_only' }, employment: { employment_type: 'full_time', start_date: monthsAgo(24) }, real_estate: { has_properties: 'yes' }, liabilities: { has_liabilities: 'yes' } } },
  { key: 'joint', label: 'Joint · PAYG + self-employed', answers: { objective: { applicant_count: 'joint' }, employment: { employment_type: 'full_time', start_date: monthsAgo(24) }, 'employment@2': { employment_type: 'self_employed', start_date: monthsAgo(36) }, 'personal@2': { first_name: 'Sam' } } },
];
